import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Bell } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatOrderNumber, type BookingDetail } from "./BookingDetailDialog";
import { STATUS_DOT_CLASS } from "./bookingsShared";

const BookingsNotifications = ({
  bookings,
  onSelect,
  onRefresh,
}: {
  bookings: BookingDetail[];
  onSelect: (b: BookingDetail) => void;
  onRefresh: () => void;
}) => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const channel = supabase
      .channel("admin-bookings-notifications")
      .on("postgres_changes", { event: "*", schema: "public", table: "bookings" }, () => {
        onRefresh();
      })
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [onRefresh]);

  const pending = useMemo(
    () =>
      bookings
        .filter((b) => b.status === "pending")
        .sort((a, b) => (a.created_at < b.created_at ? 1 : -1)),
    [bookings]
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="relative">
          <Bell className="w-4 h-4" />
          {pending.length > 0 && (
            <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-semibold flex items-center justify-center">
              {pending.length > 99 ? "99+" : pending.length}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[320px] p-0">
        <div className="px-3 py-2 border-b border-border">
          <p className="font-heading text-sm font-semibold">Réservations à confirmer</p>
          <p className="font-body text-xs text-muted-foreground">
            {pending.length} en attente de confirmation
          </p>
        </div>
        <div className="max-h-[320px] overflow-y-auto">
          {pending.length === 0 ? (
            <p className="px-3 py-6 text-center text-sm text-muted-foreground font-body">
              Aucune réservation en attente.
            </p>
          ) : (
            pending.map((b) => (
              <button
                key={b.id}
                type="button"
                onClick={() => {
                  setOpen(false);
                  onSelect(b);
                }}
                className="w-full text-left px-3 py-2.5 border-b border-border last:border-0 hover:bg-muted/60 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <span className={cn("w-2 h-2 rounded-full shrink-0", STATUS_DOT_CLASS[b.status])} />
                  <span className="font-body text-xs font-semibold">{formatOrderNumber(b.order_number)}</span>
                  <span className="font-body text-xs text-muted-foreground ml-auto">
                    {new Date(b.created_at).toLocaleDateString("fr-FR")}
                  </span>
                </div>
                <p className="font-body text-sm font-medium mt-1">{b.name}</p>
                <p className="font-body text-xs text-muted-foreground">
                  {b.service}
                  {b.pickup_location || b.dropoff_location
                    ? ` • ${b.pickup_location || "?"} → ${b.dropoff_location || "?"}`
                    : ""}
                  {b.passengers != null ? ` • ${b.passengers} pax` : ""}
                  {b.booking_date ? ` • ${new Date(b.booking_date).toLocaleDateString("fr-FR")}` : ""}
                </p>
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default BookingsNotifications;
